import { cn } from "@/lib/utils";
import type { Tables } from "@/integrations/supabase/types";
import type {
  ScheduleBooking,
  ScheduleTrainingSession,
  BookingCategory,
  CourtWithHall,
  SelectionRect,
} from "./types";
import { ScheduleGridCell } from "./ScheduleGridCell";

interface ScheduleGridRowProps {
  court: CourtWithHall;
  courtIdx: number;
  timeSlots: Date[];
  rowHeight: number;
  bookings: ScheduleBooking[];
  selectionRect: SelectionRect | null;
  courts: CourtWithHall[];
  hall: Tables<"halls"> | null;
  catByUser: Map<string, { slug: string; name: string } | null>;
  getBookingCategory: (booking: ScheduleBooking) => BookingCategory;
  getPlayerName: (booking: ScheduleBooking) => string;
  // activity-click-fix — null when the booking is not activity-linked.
  // Otherwise returns the activity title + organizer first name.
  getActivityLabel?: (
    booking: ScheduleBooking,
  ) => { title: string; organizerFirstName: string } | null;
  // schedule-grid-gap-1-fix — null when booking is not league-match-linked.
  // Otherwise returns "<home> vs <away>" + league name for purple Seriespel
  // styling. Mirrors getActivityLabel pattern.
  getLeagueMatchLabel?: (
    booking: ScheduleBooking,
  ) => { teams: string; leagueName: string } | null;
  getTrainingSlotInfo: (
    courtId: string,
    slotStart: Date,
  ) => { training: ScheduleTrainingSession; isFirst: boolean; slotCount: number } | null;
  // PR-D Layer 1 (TASK-75) — league_match span info passthrough.
  getLeagueMatchSlotInfo: (
    courtId: string,
    slotStart: Date,
  ) => { match: { id: string; scheduled_time: string; leagues: { match_duration_minutes: number | null; name: string } | null }; isFirst: boolean; slotCount: number } | null;
  // Tidsbokning Phase A — Seriespel block click handler passthrough.
  onMatchClick?: (matchId: string) => void;
  // Tidsbokning Phase D — Seriespel drag handler + drag state passthrough.
  onMatchDragStart?: (matchId: string, courtIdx: number, slotIdx: number, slotCount: number, durationMinutes: number, e: React.PointerEvent) => void;
  matchDrag?: {
    matchId: string;
    originCourtIdx: number;
    originSlotIdx: number;
    targetCourtIdx: number;
    targetSlotIdx: number;
    slotCount: number;
    durationMinutes: number;
    dropValid: boolean | null;
  } | null;
  onBookingClick: (booking: ScheduleBooking) => void;
  onDragStart: (courtIdx: number, slotIdx: number) => void;
  onDragEnter: (courtIdx: number, slotIdx: number) => void;
  onSlotClick: (courtIdx: number, slotIdx: number) => void;
  dragRef: React.MutableRefObject<{
    active: boolean;
    anchorCourtIdx: number;
    anchorSlotIdx: number;
    mode: "select" | "deselect";
  } | null>;
  // UPPDRAG-31: extended with dropValid for green/red drop-zone feedback.
  bookingDrag: {
    booking: ScheduleBooking;
    originCourtIdx: number;
    originSlotIdx: number;
    targetCourtIdx: number;
    targetSlotIdx: number;
    slotCount: number;
    dropValid: boolean | null;
  } | null;
  onBookingDragStart: (booking: ScheduleBooking, courtIdx: number, slotIdx: number, slotCount: number, e: React.PointerEvent) => void;
  // Fas 2 click-to-move UX-cleanup — pass-through to ScheduleGridCell so cells
  // can suppress drag-start when move-mode is active.
  clickToMoveBooking: ScheduleBooking | null;
  getRecurringBlockAt: (courtId: string, slotStart: Date) => any;
  getRecurringBookingAt: (courtId: string, slotStart: Date) => any;
  getSubscriptionReleaseAt: (courtId: string, slotStart: Date) => any;
  rbCustomerMap: Map<string, string>;
  onRecurringBlockClick: (block: any) => void;
  onSubscriptionClick: (sub: any) => void;
  highlighted?: boolean;
  // PR-AK — Multi-select bulk-cancel state.
  multiSelectMode?: boolean;
  bulkSelectedBookingIds?: Set<string>;
}

function isSlotSelected(
  courtIdx: number,
  slotIdx: number,
  selectionRect: SelectionRect | null,
): boolean {
  if (!selectionRect) return false;
  const minC = Math.min(selectionRect.startCourtIdx, selectionRect.endCourtIdx);
  const maxC = Math.max(selectionRect.startCourtIdx, selectionRect.endCourtIdx);
  const minS = Math.min(selectionRect.startSlotIdx, selectionRect.endSlotIdx);
  const maxS = Math.max(selectionRect.startSlotIdx, selectionRect.endSlotIdx);
  return courtIdx >= minC && courtIdx <= maxC && slotIdx >= minS && slotIdx <= maxS;
}

export function ScheduleGridRow({
  court,
  courtIdx,
  timeSlots,
  rowHeight,
  bookings,
  selectionRect,
  hall,
  catByUser,
  getBookingCategory,
  getPlayerName,
  getActivityLabel,
  getLeagueMatchLabel,
  getTrainingSlotInfo,
  getLeagueMatchSlotInfo,
  onMatchClick,
  onMatchDragStart,
  matchDrag,
  onBookingClick,
  onDragStart,
  onDragEnter,
  onSlotClick,
  dragRef,
  bookingDrag,
  onBookingDragStart,
  clickToMoveBooking,
  getRecurringBlockAt,
  getRecurringBookingAt,
  getSubscriptionReleaseAt,
  rbCustomerMap,
  onRecurringBlockClick,
  onSubscriptionClick,
  highlighted,
  multiSelectMode,
  bulkSelectedBookingIds,
}: ScheduleGridRowProps) {
  return (
    <div
      className="grid border-b border-border last:border-b-0"
      style={{
        gridTemplateColumns: `80px repeat(${timeSlots.length}, 1fr)`,
        height: `${rowHeight}px`,
        minWidth: `${80 + timeSlots.length * 22}px`,
      }}
    >
      <div className={cn("px-2 text-xs font-semibold flex items-center truncate border-r border-border sticky left-0 z-10", highlighted ? "bg-primary/20 ring-1 ring-inset ring-primary/30 text-primary" : "bg-muted/30")}>
        {court.name}
      </div>
      {timeSlots.map((slot, slotIdx) => (
        <ScheduleGridCell
          key={slot.toISOString()}
          courtId={court.id}
          courtIdx={courtIdx}
          slot={slot}
          slotIdx={slotIdx}
          bookings={bookings}
          selected={isSlotSelected(courtIdx, slotIdx, selectionRect)}
          hall={hall}
          catByUser={catByUser}
          getBookingCategory={getBookingCategory}
          getPlayerName={getPlayerName}
          getActivityLabel={getActivityLabel}
          getLeagueMatchLabel={getLeagueMatchLabel}
          getTrainingSlotInfo={getTrainingSlotInfo}
          getLeagueMatchSlotInfo={getLeagueMatchSlotInfo}
          onMatchClick={onMatchClick}
          onMatchDragStart={onMatchDragStart}
          matchDrag={matchDrag}
          onBookingClick={onBookingClick}
          onDragStart={onDragStart}
          onDragEnter={onDragEnter}
          onSlotClick={onSlotClick}
          dragRef={dragRef}
          bookingDrag={bookingDrag}
          onBookingDragStart={onBookingDragStart}
          clickToMoveBooking={clickToMoveBooking}
          getRecurringBlockAt={getRecurringBlockAt}
          getRecurringBookingAt={getRecurringBookingAt}
          getSubscriptionReleaseAt={getSubscriptionReleaseAt}
          rbCustomerMap={rbCustomerMap}
          onRecurringBlockClick={onRecurringBlockClick}
          onSubscriptionClick={onSubscriptionClick}
          multiSelectMode={multiSelectMode}
          bulkSelectedBookingIds={bulkSelectedBookingIds}
        />
      ))}
    </div>
  );
}
